// src/features/task/graphLogic.ts
// Pure helpers for TaskDependencyGraphPage: computing a focus task's
// connected neighbourhood and applying client-side filters. These exist
// because the generated GetDependencyGraph client cannot yet send
// `parent`/`max_depth` to the server — the request DTO supports them
// (see core/types/task/GetDependencyGraphRequest.ts) but the generator drops
// message-typed request fields when emitting the client function signature
// (bd framework-tdz1, fix in progress). Zero React — pure TypeScript.

export function idOf(e: unknown): string {
  const anyE = e as { entity?: { id?: string }; id?: string } | null | undefined;
  return anyE?.entity?.id || anyE?.id || '';
}

/**
 * BFS outward from `focusId` across edges in both directions (a task's
 * neighbourhood is meaningful both upstream — what it depends on — and
 * downstream — what depends on it), up to `depth` hops. `depth === null`
 * walks the entire connected component.
 */
export function walkNeighborhood(
  edges: Array<{ from_task?: unknown; to_task?: unknown }>,
  focusId: string,
  depth: number | null,
): Set<string> {
  const adj = new Map<string, Set<string>>();
  const addEdge = (a: string, b: string) => {
    if (!a || !b) return;
    if (!adj.has(a)) adj.set(a, new Set());
    adj.get(a)!.add(b);
  };
  for (const e of edges) {
    const from = idOf(e.from_task);
    const to = idOf(e.to_task);
    addEdge(from, to);
    addEdge(to, from);
  }

  const visited = new Set<string>();
  if (!focusId) return visited;
  visited.add(focusId);
  let frontier = [focusId];
  let hops = 0;
  while (frontier.length > 0 && (depth === null || hops < depth)) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const neighbor of adj.get(id) || []) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          next.push(neighbor);
        }
      }
    }
    frontier = next;
    hops++;
  }
  return visited;
}

/**
 * Best-effort parent/project id for a graph node. DependencyGraphNode does
 * not carry a `parent` field today (api/services/v1/task.proto only puts
 * `parent` on Task, not on the graph projection) — this defensively checks
 * a couple of shapes so it lights up for free the day the server response
 * grows one, and callers can tell "no data available" apart from
 * "no match" and stay honest about it in the UI.
 */
export function getNodeParentId(node: unknown): string | undefined {
  const anyNode = node as { parent?: { id?: string }; entity?: { parent?: { id?: string } } } | null | undefined;
  return anyNode?.parent?.id || anyNode?.entity?.parent?.id || undefined;
}

export function getNodeAssigneeId(node: unknown): string {
  const anyNode = node as { assignee?: { entity?: { id?: string } } } | null | undefined;
  return anyNode?.assignee?.entity?.id || '';
}
